<script setup>
/**
 * 「注册」页，逐条对齐旧版 web/33.39ea4ee523939405a30b.js 里的
 * app-register 组件（类 c、模板工厂 y + 子模板 k/v/z/x、样式 C）。
 *
 * 旧版表单（Angular FormGroup regForm）：
 *   username:    ['', [required]]
 *   nickname:    ['', [required]]
 *   password:    ['', [required]]
 *   repassword:  ['', [required]] + 自定义校验器 [equal]="password.value"
 *   proxy:       [proxy_code, [required]]
 *   recommender: [recommend]           // 无校验器
 *   // 按 user_register_option 配置动态追加：realname / mobile（均 required）
 *
 * proxy_code 逻辑（构造函数）：
 *   query/param 带 recommend → recommend=该值，recommender=该值，proxy_code=1，proxy=1
 *   query/param 带 code      → proxy_code=code，proxy=code
 *   邀请码输入项仅在 !proxy_code 时显示（*ngIf="!proxy_code"），标签 class="key" 为红色。
 *
 * ngOnInit → getConfig()：读取 user_register_option，
 *   含 'realname' 追加真实姓名项；含 'mobile' 追加手机号码项。
 *
 * register()：
 *   loading「正在注册，请稍等」
 *   regForm.invalid 直接 return
 *   _memberApi.register(regForm.value) → POST /user/register
 *   成功 → toast「注册成功」+ router.navigate(['/login'])
 *   失败 → toast(error_msg)
 *
 * 模板（display 顺序，注意与 FormGroup 声明顺序不同）：
 *   ion-header > ion-toolbar
 *     ion-buttons[slot=start] > ion-button[routerLink="/login"] > ion-icon[name=arrow-back]
 *     ion-title「注册」
 *   ion-content[padding]
 *     form > ion-list
 *       账号：/ 密码：/ 确认密码：/ 游戏昵称：
 *       [手机号码：ngIf mobile] / [真实姓名：ngIf realname] / [邀请码：ngIf !proxy_code, 红色]
 *     div.action > ion-button color="secondary" expand="full" [disabled]="!regForm.valid"「注 册」
 *     *ngIf="repassword.dirty && errors.equal" → ion-text[color=danger] > p.danger-tip「两次密码不一样」
 *
 * 样式 C 只有一条 .key{color:red}（邀请码标签用）。
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { request } from '../services/api'

const router = useRouter()
const route = useRoute()

// proxy_code / recommend 初始值：对齐旧版构造函数
const recommendFromRoute = route.params.recommender || route.query.recommend || ''
const codeFromRoute = route.query.code || ''
let initialProxyCode = ''
let initialRecommend = 0
if (recommendFromRoute) {
  initialRecommend = recommendFromRoute
  initialProxyCode = 1
}
if (codeFromRoute) {
  initialProxyCode = codeFromRoute
}

const form = reactive({
  username: '',
  nickname: '',
  password: '',
  repassword: '',
  proxy: initialProxyCode === '' ? '' : String(initialProxyCode),
  recommender: initialRecommend,
  mobile: '',
  realname: '',
})

// !proxy_code 时才显示邀请码输入项
const showInvite = computed(() => !initialProxyCode)

// user_register_option 配置项
const config = ref([])
const hasConfig = (key) => config.value.indexOf(key) !== -1
const showMobile = computed(() => hasConfig('mobile'))
const showRealname = computed(() => hasConfig('realname'))

// 确认密码是否被动过（对齐 Angular dirty）
const repasswordDirty = ref(false)
const submitting = ref(false)
const notice = ref('')

// 旧版自定义校验器 equal：repassword 必须与 password 相同
const equalError = computed(() => form.repassword !== form.password)
// 旧版 *ngIf="repassword.dirty && errors.equal"
const showEqualTip = computed(() => repasswordDirty.value && equalError.value)

// 旧版 [disabled]="!regForm.valid"：所有必填 + equal 校验通过
const formValid = computed(() => {
  if (!form.username || !form.nickname || !form.password || !form.repassword || !form.proxy) return false
  if (!/^[A-Za-z][A-Za-z0-9]{3,7}$/.test(form.username)) return false
  if (equalError.value) return false
  if (showMobile.value && !form.mobile) return false
  if (showRealname.value && !form.realname) return false
  return true
})

// 旧版 getConfig()：POST {apiUrl}/config/detail { name:'user_register_option' }
async function loadConfig() {
  try {
    const result = await request('/config/detail', {
      method: 'POST',
      data: { name: 'user_register_option' },
      anonymous: true,
    })
    if (result.error_code === 0 && Array.isArray(result.data)) config.value = result.data
  } catch (_) {
    /* 配置读取失败时按无附加项处理 */
  }
}

// 旧版 register()
async function register() {
  if (!formValid.value || submitting.value) return
  submitting.value = true
  notice.value = '正在注册，请稍等'
  try {
    await request('/user/register', {
      method: 'POST',
      data: {
        username: form.username,
        nickname: form.nickname,
        password: form.password,
        repassword: form.repassword,
        proxy: form.proxy,
        recommender: form.recommender,
        ...(showMobile.value ? { mobile: form.mobile } : {}),
        ...(showRealname.value ? { realname: form.realname } : {}),
      },
      form: true,
    })
    notice.value = '注册成功'
    window.setTimeout(() => router.replace({ name: 'login' }), 900)
  } catch (error) {
    notice.value = error.message || '注册失败，请稍后再试。'
  } finally {
    submitting.value = false
  }
}

onMounted(loadConfig)
</script>

<template>
  <div class="register-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/login" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/login" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">注册</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <form novalidate @submit.prevent="register">
        <section class="ion-list">
          <!-- 账号 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">账号：</div>
              <div class="ion-input">
                <input v-model="form.username" class="native-input" type="text" minlength="4" maxlength="8" autocomplete="username" autocapitalize="none" placeholder="4-8位英文或数字，字母开头" />
                <button v-if="form.username" class="input-clear-icon" type="button" aria-label="清除" @click="form.username = ''"></button>
              </div>
            </div>
          </div>

          <!-- 密码 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">密码：</div>
              <div class="ion-input">
                <input v-model="form.password" class="native-input" type="password" placeholder="请输入密码，6位以上" />
                <button v-if="form.password" class="input-clear-icon" type="button" aria-label="清除" @click="form.password = ''"></button>
              </div>
            </div>
          </div>

          <!-- 确认密码 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">确认密码：</div>
              <div class="ion-input">
                <input
                  v-model="form.repassword"
                  class="native-input"
                  type="password"
                  placeholder="请再输入一次密码"
                  @input="repasswordDirty = true"
                />
                <button v-if="form.repassword" class="input-clear-icon" type="button" aria-label="清除" @click="form.repassword = ''"></button>
              </div>
            </div>
          </div>

          <!-- 游戏昵称 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">游戏昵称：</div>
              <div class="ion-input">
                <input v-model="form.nickname" class="native-input" type="text" placeholder="请输入游戏昵称，聊天室使用" />
                <button v-if="form.nickname" class="input-clear-icon" type="button" aria-label="清除" @click="form.nickname = ''"></button>
              </div>
            </div>
          </div>

          <!-- 手机号码（ngIf user_register_option 含 mobile） -->
          <div v-if="showMobile" class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">手机号码：</div>
              <div class="ion-input">
                <input v-model="form.mobile" class="native-input" type="text" placeholder="取回登录密码的唯一途径，请务必真实" />
                <button v-if="form.mobile" class="input-clear-icon" type="button" aria-label="清除" @click="form.mobile = ''"></button>
              </div>
            </div>
          </div>

          <!-- 真实姓名（ngIf user_register_option 含 realname） -->
          <div v-if="showRealname" class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">真实姓名：</div>
              <div class="ion-input">
                <input v-model="form.realname" class="native-input" type="text" placeholder="必须与您的银行账户名称相同，否则不能出款" />
                <button v-if="form.realname" class="input-clear-icon" type="button" aria-label="清除" @click="form.realname = ''"></button>
              </div>
            </div>
          </div>

          <!-- 邀请码（ngIf !proxy_code，标签红色 .key） -->
          <div v-if="showInvite" class="list-item">
            <div class="list-item-inner">
              <div class="ion-label key">邀请码：</div>
              <div class="ion-input">
                <input v-model="form.proxy" class="native-input" type="text" placeholder="请输入邀请码" />
                <button v-if="form.proxy" class="input-clear-icon" type="button" aria-label="清除" @click="form.proxy = ''"></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 旧版 div.action > ion-button color="secondary" expand="full"，文本「注 册」中间带空格 -->
        <div class="action">
          <button
            class="ion-button button-full button-solid ion-color-secondary"
            type="button"
            :class="{ 'button-disabled': !formValid || submitting }"
            :disabled="!formValid || submitting"
            @click="register"
          >
            <span class="button-inner">注 册</span>
          </button>
        </div>

        <!-- 旧版 x：ion-text[color=danger] > p.danger-tip -->
        <div v-if="showEqualTip" class="ion-text-danger">
          <p class="danger-tip"> 两次密码不一样 </p>
        </div>

        <p v-if="notice" class="modal-notice">{{ notice }}</p>
      </form>
    </div>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致（同 PasswordPage） */
.register-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding] → 四边 16px */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
/* 旧版样式 C：.key{color:red}，邀请码标签用 */
.ion-label.key{color:red}

.ion-input{display:flex;align-items:center;flex:1;position:relative;padding:10px 0 11px 8px;font-size:inherit}
.ion-input .native-input{flex:1;width:100%;margin:0;padding:0;border:0;outline:none;background:transparent;color:inherit;font:inherit;box-sizing:border-box}
.ion-input .native-input::placeholder{color:currentColor;opacity:.33}
.input-clear-icon{width:30px;height:30px;border:0;background:transparent;cursor:pointer;padding:0;position:relative;opacity:.5}
.input-clear-icon::before,.input-clear-icon::after{content:"";position:absolute;left:9px;top:14px;width:12px;height:1.5px;background:currentColor}
.input-clear-icon::before{transform:rotate(45deg)}
.input-clear-icon::after{transform:rotate(-45deg)}

/* ion-button color="secondary" expand="full"：secondary → #0cd1e8；disabled → opacity .5 */
.action{margin-top:8px}
.ion-button{
  display:flex;align-items:center;justify-content:center;
  width:100%;margin:4px 0;border:0;border-radius:0;
  height:36px;font-size:14px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  background:var(--ion-color-secondary,#0cd1e8);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

/* ion-text color="danger" → --ion-color-danger #f04141 */
.ion-text-danger{color:var(--ion-color-danger,#f04141)}
.modal-notice{margin:10px 0 0;color:#999;font-size:.8rem;text-align:center}
</style>
